import React, { useEffect, useState, useCallback, useRef } from "react";

/**
 * Navigation Overlay
 *
 * This component renders an overlay that appears when the spacebar is pressed.
 * The overlay's cursor changes to a grabbing hand when the mouse is dragging.
 *
 * @param {Object} props - The component props.
 * @param {React.RefObject<HTMLElement>} props.scrollerRef - Reference to the element where scrolling will occur.
 * @returns {JSX.Element} The rendered overlay component.
 */

const NavigationOverlay = ({ scrollerRef }) => {
  const [isSpacebarPressed, setIsSpacebarPressed] = useState(false);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [startScrollPosition, setStartScrollPosition] = useState({ x: 0, y: 0 });
  const [startMousePosition, setStartMousePosition] = useState({ x: 0, y: 0 });
  const overlayRef = useRef(null);

  const handleKeyDown = useCallback((e) => {
    if (e.code === "Space" && !e.repeat && e.target === document.body) {
      e.preventDefault();
      setIsSpacebarPressed(true);
    }
  }, []);

  const handleKeyUp = useCallback((e) => {
    if (e.code === "Space") {
      setIsSpacebarPressed(false);
    }
  }, []);

  const handleMouseDown = useCallback(
    (e) => {
      if (isSpacebarPressed) {
        const scroller = scrollerRef.current;
        e.preventDefault();
        setIsMouseDown(true);
        setStartMousePosition({ x: e.clientX, y: e.clientY });
        setStartScrollPosition({
          x: scroller.scrollLeft,
          y: scroller.scrollTop,
        });
      }
    },
    [isSpacebarPressed, scrollerRef]
  );

  const handleMouseUp = useCallback(() => {
    setIsMouseDown(false);
  }, []);

  const handleMouseMove = useCallback(
    (e) => {
      if (isSpacebarPressed && isMouseDown) {
        const scroller = scrollerRef.current;
        const deltaX = e.clientX - startMousePosition.x;
        const deltaY = e.clientY - startMousePosition.y;

        scroller.scrollLeft = startScrollPosition.x - deltaX;
        scroller.scrollTop = startScrollPosition.y - deltaY;
      }
    },
    [
      isSpacebarPressed,
      isMouseDown,
      startMousePosition.x,
      startMousePosition.y,
      scrollerRef,
      startScrollPosition.x,
      startScrollPosition.y,
    ]
  );

  useEffect(() => {
    const overlay = overlayRef.current;
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("mouseup", handleMouseUp);
    overlay.addEventListener("mousedown", handleMouseDown);
    overlay.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("mouseup", handleMouseUp);
      overlay.removeEventListener("mousedown", handleMouseDown);
      overlay.removeEventListener("mousemove", handleMouseMove);
    };
  }, [handleKeyDown, handleKeyUp, handleMouseDown, handleMouseMove, handleMouseUp]);

  const overlayStyle = {
    display: isSpacebarPressed ? "block" : "none",
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    cursor: isMouseDown ? "grabbing" : "grab",
    zIndex: 99,
  };

  return <div style={overlayStyle} ref={overlayRef} data-testid="navigation-overlay" />;
};

export default NavigationOverlay;
